import { useEffect, useMemo, useState } from 'react';
import { fetchQuery, useRelayEnvironment } from 'react-relay';
import { StudentTargetFocuses, StudentTargetFocusClasses } from '../graphQLOperations/StudentTargetFocus';
import { StudentTargetFocusesQuery } from '../graphQLOperations/__generated__/StudentTargetFocusesQuery.graphql';
import { StudentTargetFocusClassesQuery } from '../graphQLOperations/__generated__/StudentTargetFocusClassesQuery.graphql';
import {
  ActiveFocusUnit,
  activeFocusUnits,
  groupFocusClassIds,
  mapPersistedFocusRows,
} from '../businessLogicService/focusService';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { setActiveFocusClassIds, hydratePersistedFocuses, selectFocuses } from '../redux/reducers/focusSlice';

export function usePersistedActiveFocus(classId: string): ActiveFocusUnit[] {
  const environment = useRelayEnvironment();
  const dispatch = useAppDispatch();
  const focusesInStore = useAppSelector(selectFocuses);
  const [activeFoci, setActiveFoci] = useState<ActiveFocusUnit[]>([]);

  useEffect(() => {
    if (!classId) {
      setActiveFoci([]);
      return;
    }
    let cancelled = false;
    fetchQuery<StudentTargetFocusesQuery>(environment, StudentTargetFocuses, {
      filter: { classIds: [classId], isActive: 'ACTIVE' },
    })
      .toPromise()
      .then(data => {
        if (cancelled) return;
        const rows = data?.studentTargetFocuses ?? [];
        // Edit settings addresses these rows by id — keep them in the store, not just
        // the collapsed per-target summary this hook returns. Merge (id-keyed, existing
        // wins) rather than replace so a stale snapshot on a class switch can't drop rows
        // an in-session activate/move/end already put in the store.
        dispatch(hydratePersistedFocuses(mapPersistedFocusRows(rows)));
        setActiveFoci(activeFocusUnits(rows));
      })
      .catch(() => undefined);
    fetchQuery<StudentTargetFocusClassesQuery>(environment, StudentTargetFocusClasses, { isActive: 'ACTIVE' })
      .toPromise()
      .then(data => {
        if (cancelled) return;
        // Server truth: the create mutation persists these rows on activation, so replacing
        // (not merging) here can't drop an in-session selection — it is already on the wire.
        dispatch(setActiveFocusClassIds(groupFocusClassIds(data?.studentTargetFocusClasses ?? [])));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [environment, dispatch, classId]);

  // The fetched list is a mount-time snapshot. Gate it on the live store so an in-session end
  // or move — which flips the target's rows to isActive:false in redux — drops the focus right
  // away, instead of the widget clinging to a stale "active" snapshot until the next reload.
  return useMemo(
    () => activeFoci.filter(f => focusesInStore.some(r => r.isActive && r.targetId === f.targetId)),
    [activeFoci, focusesInStore],
  );
}
